import { ChatViewContextGetter } from '@shared/contexts/ChatWindowContext';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useGetChatSuggestionsLazyQuery } from '@shared/generated/graphql';
import { useFeatureFlagEnabled } from '@shared/hooks/useFeatureFlagEnabled';
import type { ChatViewContext } from 'clerk_common';
import { matchDATEquipmentSearch } from 'clerk_common/constants/urls/datEquipmentSearch';
import { FeatureFlagName } from 'clerk_common/types/featureFlags';
import { useEffect, useRef, useState } from 'react';
import { ChatSuggestion, getChatSuggestions } from '../utils/chatSuggestions';

type UseChatSuggestionsOptions = {
  getSuggestionContext?: ChatViewContextGetter;
  organizationId: string;
};

type ChatSuggestionsState = {
  isLoading: boolean;
  suggestions: ChatSuggestion[];
};

export function useChatSuggestions({
  getSuggestionContext,
  organizationId,
}: UseChatSuggestionsOptions): ChatSuggestionsState {
  const { me } = useMeContext();
  const { isEnabled: areSuggestionsEnabled } = useFeatureFlagEnabled({
    name: FeatureFlagName.CHAT_SUGGESTIONS,
    organizationId,
    userId: me?.id,
  });
  const [fetchPageSuggestions] = useGetChatSuggestionsLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [pageSuggestions, setPageSuggestions] = useState<{
    organizationId: string;
    suggestions: ChatSuggestion[];
  }>();
  const requestedPageKeyRef = useRef<string | null | undefined>(undefined);
  const latestContextReadRef = useRef(0);

  useEffect(() => {
    const contextRead = latestContextReadRef.current;
    if (!areSuggestionsEnabled || !getSuggestionContext) return;

    // NOTE(wassim): Getters are recreated on every render, so refetch only when
    // the page they describe changes.
    void (async () => {
      const context = await getSuggestionContext();
      if (contextRead !== latestContextReadRef.current) return;
      const pageKey = getPageKey(organizationId, context);
      if (pageKey === requestedPageKeyRef.current) return;
      requestedPageKeyRef.current = pageKey;

      const suggestions = pageKey ? await loadPageSuggestions(context) : [];
      if (pageKey === requestedPageKeyRef.current) {
        setPageSuggestions({ organizationId, suggestions });
      }
    })();

    async function loadPageSuggestions(context: ChatViewContext) {
      setPageSuggestions(undefined);
      try {
        const { data } = await fetchPageSuggestions({
          variables: { input: { organizationId, context } },
        });
        return data?.chatSuggestions ?? [];
      } catch (error) {
        console.error(error);
        return [];
      }
    }

    return () => {
      latestContextReadRef.current++;
    };
  }, [
    areSuggestionsEnabled,
    fetchPageSuggestions,
    getSuggestionContext,
    organizationId,
  ]);

  if (!areSuggestionsEnabled) return { isLoading: false, suggestions: [] };
  if (!getSuggestionContext) {
    const organizationProducts =
      me?.organizationsDetails.find(({ id }) => id === organizationId)
        ?.products ?? [];
    return {
      isLoading: false,
      suggestions: getChatSuggestions(organizationProducts),
    };
  }
  if (pageSuggestions?.organizationId !== organizationId) {
    return { isLoading: true, suggestions: [] };
  }
  return { isLoading: false, suggestions: pageSuggestions.suggestions };
}

function getPageKey(
  organizationId: string,
  { entityIds, openEmailMessageId, pageUrl }: ChatViewContext
): string | null {
  const isDatEquipmentSearch = matchDATEquipmentSearch(pageUrl ?? '');
  const hasPage =
    Boolean(openEmailMessageId) ||
    isDatEquipmentSearch ||
    Object.values(entityIds).some((ids) => ids && ids.length > 0);
  return hasPage
    ? JSON.stringify({
        organizationId,
        entityIds,
        openEmailMessageId,
        isDatEquipmentSearch,
      })
    : null;
}
